import { html, nothing, type TemplateResult } from "lit";
import type { ManaSymbol } from "../protocol";
import { symbolText } from "./game-text";

// Names match the unmodified Arena images in client/symbols/mana (see its README).
export const ARENA_SYMBOLS = new Set([
  "W", "U", "B", "R", "G", "C", "X",
  "BG", "BR", "BU", "RU", "GU", "GW", "GR", "RW", "BW", "UW",
  "2G", "2R", "2B", "2W", "2U", "BC", "CG", "CR", "CU", "CW",
  "WP", "UP", "BP", "RP", "GP", "BGP", "BRP", "BUP", "BWP", "GRP", "GUP", "GWP", "RUP", "RWP", "UWP",
]);

const COLOR_SYMBOLS: { [symbol: string]: ManaSymbol } = {
  W: { kind: "White", values: [] },
  U: { kind: "Blue", values: [] },
  B: { kind: "Black", values: [] },
  R: { kind: "Red", values: [] },
  G: { kind: "Green", values: [] },
  C: { kind: "Colorless", values: [] },
};

function parseManaSymbol(text: string): ManaSymbol | null {
  const color = COLOR_SYMBOLS[text];
  if (color) return color;
  if (text === "X") return { kind: "XPip", values: [] };
  if (/^\d+$/.test(text)) return { kind: "Generic", values: [Number(text)] };

  if (text.endsWith("/P")) {
    const symbol = parseManaSymbol(text.slice(0, -2));
    return symbol ? { kind: "Phyrexian", values: [symbol] } : null;
  }

  const hybrid = text.split("/");
  if (hybrid.length === 2) {
    const left = parseManaSymbol(hybrid[0]!);
    const right = parseManaSymbol(hybrid[1]!);
    if (left && right) return { kind: "Hybrid", values: [left, right] };
  }
  return null;
}

function pip(symbol: ManaSymbol): TemplateResult {
  const parts = symbolText(symbol).split("/");
  const key = parts.filter((p) => p !== "P").sort().join("") + (parts.includes("P") ? "P" : "");
  const numeric = symbol.kind === "Generic" && Number.isInteger(symbol.values[0]) && symbol.values[0] >= 0 && symbol.values[0] <= 20;
  const image = numeric || ARENA_SYMBOLS.has(key) ? key : "blank";
  return html`<span class="mana-pip"><img src="./symbols/mana/${image}.png" alt="" draggable="false">${image === "blank" ? html`<span class="mana-value">${symbolText(symbol)}</span>` : nothing}</span>`;
}

export function renderManaText(text: string): TemplateResult {
  const parts = text.split(/(\{[^{}]+\})/g);
  return html`<span class="mana-text">${parts.map((part) => {
    if (!part.startsWith("{") || !part.endsWith("}")) return part;
    const symbol = parseManaSymbol(part.slice(1, -1));
    return symbol ? pip(symbol) : part;
  })}</span>`;
}

export function renderManaPips(cost: ManaSymbol[]): TemplateResult {
  return html`<span class="hand-mana" role="img" aria-label="Mana cost ${cost.map(symbolText).join(", ") || "0"}">${cost.length ? cost.map(pip) : pip({ kind: "Generic", values: [0] })}</span>`;
}
